import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Plus, Trash2, Eye, EyeOff, Save, Upload, Download, Search, Copy, FolderClock, FileText, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  getCustomers, createCustomer, updateCustomer, toggleCustomer, deleteCustomer, importCustomers,
  bulkDeleteCustomers, bulkToggleCustomers, logExport, formatApiError,
  reportCustomerStatement, reportCustomerFrequency,
} from "@/lib/api";
import { unitOf, formatDateTime } from "@/lib/fabricData";
import { CustomerFileHistory } from "@/components/CustomerFileHistory";
import { CustomerDuplicates } from "@/components/CustomerDuplicates";

const csvEsc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

export function CustomerDatabase({ token }) {
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);
  const [drafts, setDrafts] = useState({});
  const [nc, setNc] = useState({ name: "", phone: "", place: "", remark: "" });
  const [q, setQ] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [showHistory, setShowHistory] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [historyReload, setHistoryReload] = useState(0);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const res = await getCustomers(token, { q, skip: 0, limit });
      setCustomers(res.items); setTotal(res.total);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  // Debounced reload on search / batch-size change (backend queries the full DB).
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 250);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q, limit]);
  useEffect(() => { setLimit(20); /* eslint-disable-next-line */ }, [q]);

  const add = async () => {
    if (!nc.name.trim()) return toast.error("Enter customer name");
    if (nc.phone.trim() && !/^\d{10}$/.test(nc.phone.trim())) return toast.error("Phone, if entered, must be exactly 10 digits");
    try { await createCustomer(token, nc); setNc({ name: "", phone: "", place: "", remark: "" }); toast.success("Customer added"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const save = async (c) => {
    const d = drafts[c.id] || {};
    const phone = (d.phone ?? c.phone) || "";
    if (String(phone).trim() && !/^\d{10}$/.test(String(phone).trim())) return toast.error("Phone, if entered, must be exactly 10 digits");
    try {
      await updateCustomer(token, { id: c.id, name: d.name ?? c.name, phone: d.phone ?? c.phone, place: d.place ?? c.place, remark: d.remark ?? c.remark });
      setDrafts((p) => { const n = { ...p }; delete n[c.id]; return n; });
      toast.success("Customer updated"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (c) => { try { await toggleCustomer(token, c.id); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}? This cannot be undone.`)) return;
    try { await deleteCustomer(token, c.id); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setField = (id, f, v) => setDrafts((p) => ({ ...p, [id]: { ...(p[id] || {}), [f]: v } }));
  const val = (c, f) => (drafts[c.id] && drafts[c.id][f] !== undefined ? drafts[c.id][f] : (c[f] || ""));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: "" })
        .filter((r) => Array.isArray(r) && r.some((c) => String(c).trim() !== ""));
      if (rows.length < 1) { toast.error("The file is empty"); if (fileRef.current) fileRef.current.value = ""; return; }
      const header = rows[0].map((h) => String(h).trim().toLowerCase());
      const idx = (names) => header.findIndex((h) => names.includes(h));
      const iName = idx(["name", "customer name", "customer"]);
      const iPhone = idx(["phone", "phone number", "mobile", "number"]);
      const iPlace = idx(["place", "city", "location"]);
      const iRemark = idx(["remark", "remarks", "note", "notes"]);
      const hasHeader = iName !== -1 || iPhone !== -1;
      const dataRows = hasHeader ? rows.slice(1) : rows;
      const payload = dataRows.map((r) => ({
        name: String((iName !== -1 ? r[iName] : r[0]) ?? "").trim(),
        phone: String((iPhone !== -1 ? r[iPhone] : r[1]) ?? "").replace(/\.0$/, "").trim(),
        place: String((iPlace !== -1 ? r[iPlace] : r[2]) ?? "").trim(),
        remark: String((iRemark !== -1 ? r[iRemark] : r[3]) ?? "").trim(),
      }));
      const res = await importCustomers(token, payload, file.name);
      setImportResult(res);
      toast.success(`Imported ${res.inserted} · ${res.duplicates.length} duplicate(s) skipped`);
      setHistoryReload((n) => n + 1);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not read this file"); }
    if (fileRef.current) fileRef.current.value = "";
  };

  const exportCSV = async () => {
    let all = [];
    try { const res = await getCustomers(token, { q, skip: 0, limit: 0 }); all = res.items || []; }
    catch (e) { return toast.error(formatApiError(e.response?.data?.detail)); }
    if (all.length === 0) return toast.error("No customers to export");
    const rows = [["Name", "Phone", "Place", "Remark", "Status"]];
    all.forEach((c) => rows.push([c.name, c.phone, c.place, c.remark, c.active === false ? "Hidden" : "Active"]));
    const csv = rows.map((r) => r.map(csvEsc).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const filename = `customers-${new Date().toISOString().slice(0, 10)}.csv`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    try { await logExport(token, filename, all.length); setHistoryReload((n) => n + 1); } catch { /* non-blocking */ }
  };

  // ----- Bulk selection (Select All fetches the whole matching set) -----
  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = total > 0 && selected.size >= total;
  const toggleSelectAll = async () => {
    if (allSelected) { setSelected(new Set()); return; }
    try {
      const res = await getCustomers(token, { q, skip: 0, limit: 0 });
      setSelected(new Set((res.items || []).map((c) => c.id)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const clearSelection = () => setSelected(new Set());

  // Per-customer Excel downloads — fetched only when the button is tapped.
  const [busyReport, setBusyReport] = useState("");
  const downloadStatement = async (c) => {
    setBusyReport(`s:${c.id}`);
    try {
      const d = await reportCustomerStatement(token, c.id);
      const head = ["Order ID", "Date", "Status", "Company", "Fabric", "Colour", "Qty Ordered", "Qty Delivered", "Unit", "Rate", "Line Subtotal", "GST %", "Freight"];
      const rows = [head, ...(d.rows || []).map((r) => [
        r.order_id, r.date ? formatDateTime(r.date) : "", r.status, r.company_name,
        r.fabric, r.color, r.quantity_kg, r.delivered_kg ?? "", unitOf(r.fabric),
        r.rate ?? "", r.line_subtotal ?? "", r.gst_percent ?? "", r.freight ?? "",
      ])];
      const ws = XLSX.utils.aoa_to_sheet([["Customer", d.customer.name], ["Phone", d.customer.phone || ""], ["Total Orders", d.total_orders], [], ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Statement");
      const safe = (c.name || "customer").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
      XLSX.writeFile(wb, `statement-${safe}.xlsx`);
      toast.success(`Statement: ${d.total_orders} order(s)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyReport(""); }
  };
  const downloadFrequency = async (c) => {
    setBusyReport(`r:${c.id}`);
    try {
      const d = await reportCustomerFrequency(token, c.id);
      const wb = XLSX.utils.book_new();
      const summary = [["Customer", d.customer.name], ["Company", d.customer.company || ""], ["Phone", d.customer.phone || ""], ["Total Orders", d.total_orders]];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), "Summary");
      const fHead = ["Fabric", "Total Quantity", "Times Ordered"];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([fHead, ...(d.fabrics || []).map((f) => [f.fabric, f.quantity, f.count])]), "Top Fabrics");
      const cHead = ["Fabric", "Colour", "Total Quantity", "Times Ordered"];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([cHead, ...(d.colors || []).map((c2) => [c2.fabric, c2.color, c2.quantity, c2.count])]), "Top Colours");
      const safe = (c.name || "customer").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
      XLSX.writeFile(wb, `frequency-${safe}.xlsx`);
      toast.success(`Report: ${d.total_orders} order(s)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyReport(""); }
  };

  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} selected customer${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    try { await bulkDeleteCustomers(token, ids); toast.success(`Deleted ${ids.length}`); clearSelection(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const bulkSetActive = async (active) => {
    const ids = [...selected];
    if (!ids.length) return;
    try { await bulkToggleCustomers(token, ids, active); toast.success(active ? `Unhid ${ids.length}` : `Hid ${ids.length}`); clearSelection(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="customer-database">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <h2 className="font-display font-black text-2xl tracking-tight text-white">Customer Database <span className="text-white/40 text-base">({total})</span></h2>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" onChange={onFile} className="hidden" data-testid="customer-import-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="customer-import-btn" className="rounded-none h-9 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors"><Upload className="h-3.5 w-3.5 mr-2" /> Import CSV / Excel</Button>
          <Button variant="outline" onClick={exportCSV} data-testid="customer-export-btn" className="rounded-none h-9 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors"><Download className="h-3.5 w-3.5 mr-2" /> Export</Button>
          <Button variant="outline" onClick={() => setShowDuplicates(true)} data-testid="customer-find-duplicates-btn" className="rounded-none h-9 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors"><Copy className="h-3.5 w-3.5 mr-2" /> Find Duplicates</Button>
          <Button variant="outline" onClick={() => setShowHistory((v) => !v)} data-testid="customer-history-toggle" className={`rounded-none h-9 border-white/20 bg-transparent hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors ${showHistory ? "bg-white text-black" : "text-white"}`}><FolderClock className="h-3.5 w-3.5 mr-2" /> File History</Button>
        </div>
      </div>

      {showHistory && <CustomerFileHistory token={token} reloadToken={historyReload} />}

      {/* Add customer */}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 p-4 border border-white/10 bg-[#0A0A0A]">
        <Input value={nc.name} onChange={(e) => setNc((p) => ({ ...p, name: e.target.value }))} placeholder="Name *" data-testid="new-customer-name" className="rounded-none h-9 bg-[#141414] border-white/10 text-white text-sm" />
        <Input value={nc.phone} onChange={(e) => setNc((p) => ({ ...p, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))} placeholder="Phone (optional, 10 digits)" data-testid="new-customer-phone" className="rounded-none h-9 bg-[#141414] border-white/10 text-white text-sm" />
        <Input value={nc.place} onChange={(e) => setNc((p) => ({ ...p, place: e.target.value }))} placeholder="Place" data-testid="new-customer-place" className="rounded-none h-9 bg-[#141414] border-white/10 text-white text-sm" />
        <Input value={nc.remark} onChange={(e) => setNc((p) => ({ ...p, remark: e.target.value }))} placeholder="Remark" data-testid="new-customer-remark" className="rounded-none h-9 bg-[#141414] border-white/10 text-white text-sm" />
        <Button onClick={add} data-testid="add-customer-btn" className="rounded-none h-9 bg-white text-black hover:bg-white/90 text-xs uppercase tracking-wider"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </div>

      {importResult && (
        <div className="border border-white/10 bg-[#0A0A0A] p-4" data-testid="import-result">
          <p className="text-sm text-white mb-2">Import: <span className="text-emerald-400">{importResult.inserted} added</span>, <span className="text-red-400">{importResult.duplicates.length} duplicate(s)</span>{importResult.invalid?.length ? <span className="text-amber-400">, {importResult.invalid.length} invalid</span> : null}</p>
          {importResult.duplicates.length > 0 && (
            <div className="space-y-1 max-h-48 overflow-auto">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">Duplicates (not imported)</p>
              {importResult.duplicates.map((d, i) => (
                <div key={i} data-testid={`import-dup-${i}`} className="text-xs text-red-400 border border-red-500/30 bg-red-500/10 px-2 py-1">{d.name} {d.phone ? `· ${d.phone}` : ""} {d.place ? `· ${d.place}` : ""} <span className="text-red-300/60">(matched by {d.reason || "phone"})</span></div>
              ))}
            </div>
          )}
          <button onClick={() => setImportResult(null)} className="mt-2 text-[10px] uppercase tracking-wider text-white/40 hover:text-white" data-testid="import-result-dismiss">Dismiss</button>
        </div>
      )}

      <div className="relative w-full sm:w-96">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…" data-testid="customer-search" className="rounded-none h-10 bg-[#141414] border-white/10 pl-10 text-white text-sm" />
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 border border-white/15 bg-[#141414] px-3 py-2" data-testid="customer-bulk-bar">
          <span className="text-xs text-white/70" data-testid="customer-bulk-count">{selected.size} selected</span>
          <div className="flex-1" />
          <Button onClick={bulkDelete} variant="outline" className="rounded-none h-8 border-red-500/40 bg-transparent text-red-400 hover:bg-red-500 hover:text-white text-xs uppercase tracking-wider" data-testid="customer-bulk-delete"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
          <Button onClick={() => bulkSetActive(false)} variant="outline" className="rounded-none h-8 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider" data-testid="customer-bulk-hide"><EyeOff className="h-3.5 w-3.5 mr-1" /> Hide</Button>
          <Button onClick={() => bulkSetActive(true)} variant="outline" className="rounded-none h-8 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider" data-testid="customer-bulk-unhide"><Eye className="h-3.5 w-3.5 mr-1" /> Unhide</Button>
          <Button onClick={clearSelection} variant="outline" className="rounded-none h-8 border-white/20 bg-transparent text-white/60 hover:bg-white hover:text-black text-xs uppercase tracking-wider" data-testid="customer-bulk-clear">Clear</Button>
        </div>
      )}

      <div className="border border-white/10 divide-y divide-white/10">
        <div className="flex items-center gap-3 px-4 py-2 bg-[#141414]">
          <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} data-testid="customer-select-all" className="accent-white h-4 w-4 shrink-0" title="Select all" />
          <div className="hidden sm:grid grid-cols-12 gap-2 flex-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">
            <div className="col-span-3">Name</div><div className="col-span-2">Phone</div><div className="col-span-2">Place</div><div className="col-span-3">Remark</div><div className="col-span-2 text-right">Actions</div>
          </div>
          <span className="sm:hidden text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">Select all</span>
        </div>
        {total === 0 && <div className="px-4 py-6 text-sm text-white/40">No customers.</div>}
        {customers.map((c) => (
          <div key={c.id} data-testid={`customer-row-${c.id}`} className={`flex items-start gap-3 px-4 py-3 ${c.active === false ? "opacity-50" : ""} ${!/^\d{10}$/.test(String(c.phone || "")) ? "border-l-2 border-amber-500" : ""} ${selected.has(c.id) ? "bg-white/5" : ""}`}>
            <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} data-testid={`customer-select-${c.id}`} className="accent-white h-4 w-4 shrink-0 mt-2" />
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center flex-1 min-w-0">
              <Input value={val(c, "name")} onChange={(e) => setField(c.id, "name", e.target.value)} className="sm:col-span-3 rounded-none h-8 bg-[#141414] border-white/10 text-white text-sm" data-testid={`customer-name-${c.id}`} />
              <div className="sm:col-span-2 flex items-center gap-1">
                <Input value={val(c, "phone")} onChange={(e) => setField(c.id, "phone", e.target.value.replace(/\D/g, "").slice(0, 10))} className="flex-1 rounded-none h-8 bg-[#141414] border-white/10 text-white text-sm" data-testid={`customer-phone-${c.id}`} />
                {!/^\d{10}$/.test(String(c.phone || "")) && <span title="Phone missing or not 10 digits" data-testid={`customer-phone-flag-${c.id}`} className="text-amber-400 text-xs shrink-0">⚠</span>}
              </div>
              <Input value={val(c, "place")} onChange={(e) => setField(c.id, "place", e.target.value)} className="sm:col-span-2 rounded-none h-8 bg-[#141414] border-white/10 text-white text-sm" data-testid={`customer-place-${c.id}`} />
              <Input value={val(c, "remark")} onChange={(e) => setField(c.id, "remark", e.target.value)} className="sm:col-span-3 rounded-none h-8 bg-[#141414] border-white/10 text-white text-sm" data-testid={`customer-remark-${c.id}`} />
              <div className="sm:col-span-2 flex gap-1 justify-end flex-wrap">
                <Button onClick={() => downloadStatement(c)} disabled={busyReport === `s:${c.id}`} title="Download order statement (Excel)" data-testid={`customer-statement-${c.id}`} variant="outline" className="rounded-none h-8 w-8 p-0 border-sky-500/40 bg-transparent text-sky-300 hover:bg-sky-500 hover:text-black disabled:opacity-40"><FileText className="h-3.5 w-3.5" /></Button>
                <Button onClick={() => downloadFrequency(c)} disabled={busyReport === `r:${c.id}`} title="Download order frequency report (Excel)" data-testid={`customer-report-${c.id}`} variant="outline" className="rounded-none h-8 w-8 p-0 border-violet-500/40 bg-transparent text-violet-300 hover:bg-violet-500 hover:text-black disabled:opacity-40"><BarChart3 className="h-3.5 w-3.5" /></Button>
                <Button onClick={() => save(c)} data-testid={`customer-save-${c.id}`} variant="outline" className="rounded-none h-8 w-8 p-0 border-white/20 bg-transparent text-white hover:bg-white hover:text-black"><Save className="h-3.5 w-3.5" /></Button>
                <Button onClick={() => toggle(c)} data-testid={`customer-toggle-${c.id}`} variant="outline" className="rounded-none h-8 w-8 p-0 border-white/20 bg-transparent text-white hover:bg-white hover:text-black">{c.active === false ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</Button>
                <Button onClick={() => remove(c)} data-testid={`customer-delete-${c.id}`} variant="outline" className="rounded-none h-8 w-8 p-0 border-red-500/40 bg-transparent text-red-400 hover:bg-red-500 hover:text-white"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-1" data-testid="customer-pagination">
        <span className="text-xs text-white/50" data-testid="customer-page-info">Showing {customers.length} of {total}</span>
        {customers.length < total && (
          <Button onClick={() => setLimit((l) => l + 20)} variant="outline" data-testid="customer-load-more" className="rounded-none h-9 px-6 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors">
            Load More ({total - customers.length} more)
          </Button>
        )}
      </div>

      <CustomerDuplicates token={token} open={showDuplicates} onOpenChange={setShowDuplicates} onChanged={load} />
    </div>
  );
}
